import { useQuery } from "@tanstack/react-query";
import { getMyStreaks } from "@/lib/social.functions";
import { getMyBracket } from "@/lib/bracket.functions";
import { getMyViralStats } from "@/lib/viralReferral.functions";
import { resolveFanTitle } from "@/lib/fanTitles";
import { useLanguage } from "@/hooks/useLanguage";
import { useAuth } from "@/hooks/useAuth";

const GLOW: Record<string, string> = {
  amber: "oklch(0.82 0.18 75)",
  rose: "oklch(0.65 0.27 27)",
  cyan: "oklch(0.75 0.18 210)",
  violet: "oklch(0.65 0.22 295)",
  lime: "oklch(0.82 0.2 130)",
};

export function FanTitleCard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const streaks = useQuery({ queryKey: ["social", "streaks"], queryFn: () => getMyStreaks() });
  const bracket = useQuery({ queryKey: ["bracket", "me"], queryFn: () => getMyBracket() });
  const viral = useQuery({ queryKey: ["viral", "me", user?.id], queryFn: () => getMyViralStats(), enabled: !!user });

  const sList = streaks.data?.streaks ?? [];
  const predStreak = Number(sList.find((s) => s.streak_type === "prediction")?.current_count ?? 0);
  const hypeStreak = Number(sList.find((s) => s.streak_type === "hype")?.current_count ?? 0);
  const preds = (bracket.data?.predictions ?? []) as Array<{ is_exact: boolean; points_awarded: number }>;
  const exact = preds.filter((p) => p.is_exact).length;
  const totalPoints = preds.reduce((acc, p) => acc + (p.points_awarded ?? 0), 0);
  const refs = Number(viral.data?.registered ?? 0);

  const title = resolveFanTitle({
    exactPredictions: exact,
    totalPoints,
    hypeStreak,
    predictionStreak: predStreak,
    referralCount: refs,
    shareCount: refs,
  });
  const glow = GLOW[title.glow];

  return (
    <section
      className="rounded-2xl border p-4"
      style={{ borderColor: glow, background: `linear-gradient(135deg, ${glow}22, transparent)`, boxShadow: `0 0 28px ${glow}33` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("fan_title_label")}</div>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-3xl">{title.emoji}</span>
        <span className="text-xl font-black">{t(title.key)}</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[10px]">
        <Stat label={t("fan_title_exact")} value={exact} />
        <Stat label={t("fan_title_points")} value={totalPoints} />
        <Stat label={t("fan_title_referrals")} value={refs} />
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 px-2 py-1">
      <div className="uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-base font-black tabular-nums">{value}</div>
    </div>
  );
}